import React, { useState, useEffect } from 'react';
import { X, Clock, FileText, MapPin, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { appointmentService } from '../../../services/appointmentService';
import { contactService } from '../../../services/contactService';
import { useAuth } from '../../../contexts/AuthContext';

export default function AppointmentModal({ appointment, services, onSave, onDelete, onClose }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contactId: '',
    serviceId: '',
    assignedTo: '',
    startTime: '',
    endTime: '',
    status: 'scheduled',
    recurrence: 'none',
    recurrenceEndDate: '',
    notes: '',
    location: ''
  });

  const tenantId = user?.user_metadata?.tenant_id || user?.raw_user_meta_data?.tenant_id;

  useEffect(() => {
    loadCustomers();
    if (appointment) {
      setFormData({
        title: appointment?.title || '',
        description: appointment?.description || '',
        contactId: appointment?.contactId || '',
        serviceId: appointment?.serviceId || '',
        assignedTo: appointment?.assignedTo || '',
        startTime: appointment?.startTime ? format(new Date(appointment.startTime), "yyyy-MM-dd'T'HH:mm") : '',
        endTime: appointment?.endTime ? format(new Date(appointment.endTime), "yyyy-MM-dd'T'HH:mm") : '',
        status: appointment?.status || 'scheduled',
        recurrence: appointment?.recurrence || 'none',
        recurrenceEndDate: appointment?.recurrenceEndDate || '',
        notes: appointment?.notes || '',
        location: appointment?.location || ''
      });
    } else if (appointment?.startTime) {
      const startTime = new Date(appointment.startTime);
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);
      setFormData(prev => ({
        ...prev,
        startTime: format(startTime, "yyyy-MM-dd'T'HH:mm"),
        endTime: format(endTime, "yyyy-MM-dd'T'HH:mm")
      }));
    }
  }, [appointment]);

  const loadCustomers = async () => {
    try {
      const data = await contactService?.getCustomers(tenantId);
      setCustomers(data);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'serviceId') {
      const selectedService = services?.find(s => s?.id === value);
      if (selectedService && formData?.startTime) {
        const start = new Date(formData.startTime);
        const end = new Date(start.getTime() + selectedService.duration * 60 * 1000);
        setFormData(prev => ({
          ...prev,
          endTime: format(end, "yyyy-MM-dd'T'HH:mm")
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.title?.trim()) {
      setError('Appointment title is required');
      setLoading(false);
      return;
    }
    
    if (!formData?.startTime || !formData?.endTime) {
      setError('Start time and end time are required');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      if (formData?.startTime && formData?.endTime) {
        const conflicts = await appointmentService?.checkConflicts(
          formData?.startTime,
          formData?.endTime,
          formData?.assignedTo,
          appointment?.id
        );

        if (conflicts && conflicts?.length > 0) {
          setError(`Time slot conflict detected with: ${conflicts?.[0]?.title}`);
          setLoading(false);
          return;
        }
      }

      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save appointment');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) {
      return;
    }

    try {
      setLoading(true);
      await onDelete(appointment?.id);
    } catch (err) {
      setError(err?.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {appointment?.id ? 'Edit Appointment' : 'New Appointment'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData?.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter appointment title"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact *
              </label>
              <select
                name="contactId"
                value={formData?.contactId}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select contact</option>
                {customers?.map(customer => (
                  <option key={customer?.id} value={customer?.id}>
                    {customer?.companyName || customer?.contactPerson}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service
              </label>
              <select
                name="serviceId"
                value={formData?.serviceId}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select service</option>
                {services?.map(service => (
                  <option key={service?.id} value={service?.id}>
                    {service?.name} ({service?.duration} min)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Start Time *
              </label>
              <input
                type="datetime-local"
                name="startTime"
                value={formData?.startTime}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                End Time *
              </label>
              <input
                type="datetime-local"
                name="endTime"
                value={formData?.endTime}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData?.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No Show</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData?.location}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter location"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Notes
            </label>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add notes..."
            />
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div>
              {appointment?.id && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}